
import { defineComponent, ref, computed, onMounted, getCurrentInstance } from 'vue';
import { convertToHtmlHelper } from '@/heplers';
import StatusBadge from '../../ui/StatusBadge.vue';
import ImgFullscreen from '../../ui/ImgFullscreen.vue';
import LanguageInfo from './LanguageInfo.vue';
import CodeExampleBody from '@/components/docs-main/code-rails-view/CodeExampleBody.vue';
import type { TutorialType } from '@/templates/Tutorial';
import CopyUrlButton from '@/components/ui/buttons/CopyUrlButton.vue';
import { convertStringToAnchorIdNoHashHelper } from '@/heplers/stringHelpers';
import Prism from 'prismjs';
import { useCurrentCodeBlock } from '@/heplers/composables';
import CodeBlockAnchor from '@/components/docs-main/code-rails-view/CodeBlockAnchor.vue';
import { BadgesType } from '@/templates/References';
import { Badge, getBadgeKeys } from '@/components/docs-main/reference-pages/badge';

interface StoreProxy {
  getters: Record<string, unknown>;
  state: Record<string, unknown>;
  commit: (mutation: string, payload?: unknown) => void;
}

export default defineComponent({
  name: 'Tutorial',
  components: {
    Badge,
    CodeBlockAnchor,
    StatusBadge,
    LanguageInfo,
    CodeExampleBody,
    CopyUrlButton,
    ImgFullscreen,
  },
  setup() {
    const instance = getCurrentInstance();
    const store = computed<StoreProxy | undefined>(
      () => instance?.proxy?.$store as StoreProxy | undefined
    );

    const { setCurrentCodeId } = useCurrentCodeBlock();

    const languages = ref<string[]>([]);

    const data = computed<TutorialType>(
      () => (store.value?.getters?.getContent as TutorialType) ?? ({} as TutorialType)
    );

    const attributesExist = computed(
      () => data.value?.attributes && !Array.isArray(data.value.attributes)
    );

    /**
     * Собираем объект для alert блоков
     */
    const statusBadges = computed(() => {
      if (!data.value?.attributes) return {};

      const allowedBadgeNames: BadgesType[] = Object.values(BadgesType);

      const filter = (
        attributes: Record<string, unknown>,
        allowed: BadgesType[]
      ): Record<string, string> =>
        allowed.reduce<Record<string, string>>((acc, name) => {
          if (attributes[name] && typeof attributes[name] === 'string') {
            acc[name] = attributes[name] as string;
          }
          return acc;
        }, {});

      return filter(data.value.attributes as Record<string, unknown>, allowedBadgeNames);
    });

    const checkTargetLink = (link: string) => (link.includes('http') ? '_blank' : '_self');

    const showCodeExample = (obj: { kind?: string; title?: string }) =>
      obj.kind === 'content_source' && !!obj.title;

    const convertToHtml = (mdString: string) => {
      if (!mdString) return '';
      return convertToHtmlHelper(mdString);
    };

    const toAnchorIdNoHash = (text: string) => convertStringToAnchorIdNoHashHelper(text);

    const dataBadges = computed(() => getBadgeKeys(data.value));

    onMounted(() => {
      Prism.highlightAll();
    });

    return {
      data,
      languages,
      attributesExist,
      statusBadges,
      dataBadges,
      checkTargetLink,
      convertToHtml,
      showCodeExample,
      toAnchorIdNoHash,
      setCurrentCodeId,
    };
  },
});
